import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment as env } from '@macs-frontend/env';

const REFRESH_BUFFER_SECONDS = 60;

/**
 * Anonymous pages where Keycloak must never run. Visitors arrive via
 * emailed links (password reset, estimate/invoice approval, invitations)
 * and typically have no account at all — letting keycloak-js init() run
 * with onLoad: 'login-required' would trap them on the KC login page.
 * These pages authenticate via their own signed URL token instead.
 */
const PUBLIC_PATH_PREFIXES = ['/auth/reset/', '/public/'];

@Injectable({ providedIn: 'root' })
export class KeycloakService {
  private keycloak: Keycloak | null = null;
  private _authenticated = false;
  private initPromise: Promise<boolean> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /** A login redirect is already under way — don't start a second one. */
  private redirecting = false;

  constructor() {
    if (env.keycloak) {
      // Evict any legacy json_login JWT left in this browser. Keycloak is the
      // only issuer these builds talk to, and a leftover Lexik token is not
      // inert: JwtModule's interceptor stamped it onto every same-origin
      // request, and anything else reading localStorage.token would send it
      // too. The backend answers such a token with 401 "Signature
      // verification failed" — see tokenGetter in auth.module.ts.
      localStorage.removeItem('token');

      this.keycloak = new Keycloak({
        url: env.keycloak.url,
        realm: env.keycloak.realm,
        clientId: env.keycloak.clientId,
      });
    }
  }

  get enabled(): boolean {
    return this.keycloak != null;
  }

  get authenticated(): boolean {
    return this._authenticated;
  }

  get token(): string | undefined {
    return this.keycloak?.token;
  }

  get tokenParsed(): Record<string, unknown> | undefined {
    return this.keycloak?.tokenParsed as Record<string, unknown> | undefined;
  }

  get username(): string | undefined {
    return this.tokenParsed?.['preferred_username'] as string | undefined;
  }

  /** Keycloak `sub` — the user's UUID, used as the messaging sender/current-user id. */
  get userId(): string | undefined {
    return this.tokenParsed?.['sub'] as string | undefined;
  }

  get company(): string | undefined {
    return (this.tokenParsed?.['company'] ?? this.tokenParsed?.['companyUuid']) as
      | string
      | undefined;
  }

  get groups(): string[] {
    const g = this.tokenParsed?.['groups'];
    return Array.isArray(g) ? (g as string[]) : [];
  }

  /**
   * Slim JWT: per-company role codes from the company_roles claim.
   * Structure: { "company-uuid": ["admin", "lead"], ... }
   */
  get companyRoles(): Record<string, string[]> | null {
    const cr = this.tokenParsed?.['company_roles'];
    return cr != null && typeof cr === 'object' && !Array.isArray(cr)
      ? (cr as Record<string, string[]>)
      : null;
  }

  async init(): Promise<boolean> {
    if (!this.keycloak) return false;
    if (this.initPromise != null) return this.initPromise;

    if (
      typeof window !== 'undefined' &&
      PUBLIC_PATH_PREFIXES.some(p => window.location.pathname.startsWith(p))
    ) {
      this._authenticated = false;
      return false;
    }

    this.initPromise = (async () => {
      try {
        this._authenticated = await this.keycloak!.init({
          onLoad: 'login-required',
          flow: 'standard',
          pkceMethod: 'S256',
          checkLoginIframe: false,
        });

        if (this._authenticated) {
          this.setupTokenExpiry();
          this.setupVisibilityRefresh();
          this.scheduleTokenRefresh();
        }

        return this._authenticated;
      } catch (err) {
        console.error('Keycloak init failed', err);
        return false;
      }
    })();
    return this.initPromise;
  }

  /**
   * Starts a Keycloak login redirect — at most once. Several call sites react
   * to auth failures independently (interceptors, the expiry handler, guards)
   * and they can fire in the same tick; each extra call restarts a navigation
   * that is already leaving the page, which is how one failure turned into a
   * visible reload storm.
   */
  login(redirectUri?: string): Promise<void> {
    if (this.redirecting) return Promise.resolve();
    this.redirecting = true;
    return (
      this.keycloak?.login({ redirectUri: redirectUri ?? window.location.href }) ??
      Promise.resolve()
    );
  }

  logout(redirectUri?: string): Promise<void> {
    if (this.redirecting) return Promise.resolve();
    this.redirecting = true;
    this.clearRefreshTimer();
    return (
      this.keycloak?.logout({ redirectUri: redirectUri ?? window.location.origin }) ??
      Promise.resolve()
    );
  }

  async getToken(): Promise<string> {
    if (!this.keycloak) return '';
    // Init was skipped (public page) or the user never authenticated:
    // there is no session to refresh, and calling updateToken() on an
    // uninitialized keycloak-js instance rejects — which would bounce
    // anonymous visitors to the KC login page. Send requests unauthenticated.
    if (!this._authenticated) return '';
    try {
      await this.keycloak.updateToken(30);
      this.scheduleTokenRefresh();
      return this.keycloak.token ?? '';
    } catch {
      console.warn('Token refresh failed, redirecting to login');
      await this.login(window.location.href);
      return '';
    }
  }

  private setupTokenExpiry(): void {
    if (!this.keycloak) return;
    this.keycloak.onTokenExpired = () => {
      console.log('Keycloak token expired, attempting refresh');
      this.keycloak!.updateToken(30)
        .then(refreshed => {
          if (refreshed) {
            console.log('Token refreshed after expiry');
          }
          // Re-arm unconditionally. updateToken() resolves false when the token
          // is still considered valid, and skipping the reschedule there left
          // the app with no proactive refresh at all for the rest of the session.
          this.scheduleTokenRefresh();
        })
        .catch(() => {
          console.warn('Token refresh failed after expiry, redirecting to login');
          this.login(window.location.href);
        });
    };
  }

  /**
   * Browsers throttle and eventually freeze timers in background tabs, so
   * `scheduleTokenRefresh`'s timeout cannot be relied on while the user is
   * away — the tab can come back with a long-expired access token and a
   * refresh timer that never fired. Refresh as soon as the page is visible
   * again, which also re-arms the timer for the active period that follows.
   */
  private setupVisibilityRefresh(): void {
    if (typeof document === 'undefined') return;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this._authenticated) {
        this.getToken();
      }
    });
  }

  private scheduleTokenRefresh(): void {
    this.clearRefreshTimer();
    if (!this.keycloak?.tokenParsed?.['exp']) return;

    const exp = (this.keycloak.tokenParsed as Record<string, unknown>)['exp'] as number;
    const nowSec = Math.floor(Date.now() / 1000);
    const refreshInMs = Math.max((exp - nowSec - REFRESH_BUFFER_SECONDS) * 1000, 0);

    if (refreshInMs > 0) {
      this.refreshTimer = setTimeout(() => {
        console.log('Proactive token refresh triggered');
        this.getToken();
      }, refreshInMs);
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
